import { type RefObject } from 'react';
import {
  decodeProfileValues,
  getExposePath,
  type GenericSearchListing,
  type Language,
  type MarketCountryCode,
} from '@ev-tech/search-modules-api';

import { type SearchResultCardForwardedLinkEvent } from './SearchResultCard.types';
import { getUrlWithParams } from '../../utils/searchParams';

type GetSearchResultCardLinkParams = {
  listing: GenericSearchListing;
  formattedLocation?: string;
  language: Language;
  countryCode: MarketCountryCode;
  hrefBaseUrl?: URL;
  linkParams?: Record<string, string>;
  trackingQueryParams?: Record<string, string>;
};

export const getSearchResultCardLink = ({
  listing,
  formattedLocation,
  language,
  countryCode,
  hrefBaseUrl,
  linkParams,
  trackingQueryParams,
}: GetSearchResultCardLinkParams) => {
  const title = listing.profile ? decodeProfileValues(listing.profile).title : undefined;
  const exposePath = getExposePath(listing, language, countryCode);
  const promotionAgentId = 'promotionAgentId' in listing ? listing.promotionAgentId : undefined;

  const label = title || (formattedLocation ? `Property in ${formattedLocation}` : '');

  let linkURL = exposePath;

  if (hrefBaseUrl) {
    const exposeUrl = new URL(hrefBaseUrl.href);
    exposeUrl.pathname = exposePath;
    linkURL = exposeUrl.href;
  }

  const exposeParams = {
    ...(promotionAgentId && { promotedBy: promotionAgentId }),
    ...(linkParams && { ...linkParams }),
    ...(trackingQueryParams && { ...trackingQueryParams }),
  };

  const href = getUrlWithParams(linkURL, exposeParams);

  return { href: href || linkURL, label };
};

export const forwardSearchResultCardLinkEvent = ({
  event,
  linkRef,
}: {
  event: SearchResultCardForwardedLinkEvent;
  linkRef: RefObject<HTMLSpanElement>;
}) => {
  if (('key' in event && event.key === 'Enter' && event.currentTarget === document.activeElement) || !('key' in event)) {
    event.stopPropagation();
    event.preventDefault();
    linkRef.current?.click();
  }
};
